'use client'

import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useState } from 'react'
import { useCIAM } from 'security/hooks/CIAM/useCIAM/useCIAM'
import { ActionTypes } from 'security/types/CIAM'
import { useSetSession } from 'session/src/hooks/useSetSession'
import { setSessionCacheStorage } from 'session/src/utils/cacheStorage/setSessionCacheStorage'
import { userResponseToSession } from 'session/src/utils/userResponseToSession'

import { FormFieldNames } from '../constants/form'
import { registryService } from '../services/registryService'
import type { RegistryErrors, RegistryManager } from '../types/registry'
import type {
	RegistryInputs,
	RegistryOptions,
	RegistryServiceParams,
} from '../types/registryService'
import { isRegistryFieldsError } from '../utils/isRegistryFieldsError'

export const useRegistry = (): RegistryManager => {
	const masterData = useMasterData()
	const { device, country, channel } = masterData
	const { action, auth } = useCIAM()

	const { setSession } = useSetSession()

	const [registryErrors, setRegistryErrors] = useState<RegistryErrors>(null)
	const [registrySuccess, setRegistrySuccess] = useState(false)

	if (!setSession) {
		throw new Error('useRegistry must be used within a SessionProvider')
	}

	const registry = async (
		{
			email,
			password,
			phone,
			newsletterSubscription,
			birthDate,
		}: RegistryInputs,
		{ origin }: RegistryOptions
	): Promise<void> => {
		const payload: RegistryServiceParams & {
			[key in FormFieldNames]?: string
		} = {
			email,
			channel,
			origin,
			password,
			countryIso3166: country.countryISO,
			deviceType: device,
			language: country.languageISO,
			mobile: phone.number,
			mobileCountry: phone.countryIso2,
			newsletter: newsletterSubscription,
			prefix: phone.prefix,
		}

		if (birthDate) {
			payload[FormFieldNames.BirthDate] = birthDate
		}

		await action(ActionTypes.REGISTER)

		const response = await registryService(payload)

		if (!response) {
			setRegistryErrors(['generic'])
		} else if (isRegistryFieldsError(response)) {
			setRegistryErrors(Object.keys(response.errorFields) as RegistryErrors)
		} else {
			const sessionState = userResponseToSession(response)

			auth(sessionState.accountId)
			setSession(sessionState)

			await setSessionCacheStorage(sessionState, masterData)

			setRegistrySuccess(true)
		}
	}

	return {
		registry,
		registryErrors,
		registrySuccess,
	}
}
